import api from "./api";
import type {
  WorkItemCreateRequest,
  WorkItemDayView,
  WorkItemDetail,
  WorkItemLinkCandidate,
  WorkItemSearchParams,
  WorkItemSearchResult,
  WorkItemSourceType,
  TeamDayView,
  TimelineView,
  MonthView,
} from "@/types/worklog";

function downloadBlob(response: { data: BlobPart; headers: Record<string, unknown> }, fallback: string) {
  const blob = new Blob([response.data], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  const cd = response.headers["content-disposition"] as string | undefined;
  let filename = fallback;
  if (cd) {
    const utf8 = cd.match(/filename\*=UTF-8''([^;]+)/i);
    const plain = cd.match(/filename="?([^";]+)"?/i);
    if (utf8) filename = decodeURIComponent(utf8[1]);
    else if (plain) filename = plain[1];
  }
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

const BASE = "/work-items";

// 일일보고 업무 항목(진행 → 완료 추적). 모든 변경 응답은 해당 날짜 화면 전체(WorkItemDayView)
export const workItemService = {
  async getDay(date: string): Promise<WorkItemDayView> {
    const res = await api.get<WorkItemDayView>(`${BASE}/day`, { params: { date } });
    return res.data;
  },

  async create(data: WorkItemCreateRequest): Promise<WorkItemDayView> {
    const res = await api.post<WorkItemDayView>(BASE, data);
    return res.data;
  },

  async update(
    id: number,
    data: {
      date: string;
      title?: string;
      source_type?: WorkItemSourceType | null;
      source_id?: string | null;
      clear_source?: boolean;
    }
  ): Promise<WorkItemDayView> {
    const res = await api.patch<WorkItemDayView>(`${BASE}/${id}`, data);
    return res.data;
  },

  async setWorked(id: number, date: string, worked: boolean): Promise<WorkItemDayView> {
    const res = await api.put<WorkItemDayView>(`${BASE}/${id}/worked`, { date, worked });
    return res.data;
  },

  // resolveSource=true 면 연결된 이슈/칸반/하달도 함께 완료 처리(기존 완료 경로 호출)
  // 그날 진행중 항목 전부 '작업함' 체크
  async workedAll(date: string): Promise<WorkItemDayView> {
    const res = await api.put<WorkItemDayView>(`${BASE}/day/worked-all`, { date });
    return res.data;
  },

  // 진행중 → 예정으로 되돌리기(방치 항목 정리)
  async toPlanned(id: number, date: string): Promise<WorkItemDayView> {
    const res = await api.post<WorkItemDayView>(`${BASE}/${id}/to-planned`, { date });
    return res.data;
  },

  async complete(id: number, date: string, resolveSource = false): Promise<WorkItemDayView> {
    const res = await api.post<WorkItemDayView>(`${BASE}/${id}/complete`, {
      date,
      resolve_source: resolveSource,
    });
    return res.data;
  },

  async reopen(id: number, date: string): Promise<WorkItemDayView> {
    const res = await api.post<WorkItemDayView>(`${BASE}/${id}/reopen`, { date });
    return res.data;
  },

  async remove(id: number, date: string): Promise<WorkItemDayView> {
    const res = await api.delete<WorkItemDayView>(`${BASE}/${id}`, { params: { date } });
    return res.data;
  },

  // 여러 줄 붙여넣기 → 줄마다 항목 등록
  async createBulk(date: string, titles: string[], status: "in_progress" | "planned" = "in_progress"): Promise<WorkItemDayView> {
    const res = await api.post<WorkItemDayView>(`${BASE}/bulk`, { date, titles, status });
    return res.data;
  },

  // 그날 이 항목에서 한 일 메모(여러 줄). 메모를 쓰면 그날 작업한 것으로 기록
  async setDayNote(id: number, date: string, note: string): Promise<WorkItemDayView> {
    const res = await api.put<WorkItemDayView>(`${BASE}/${id}/day-note`, { date, note });
    return res.data;
  },

  async updateNotes(date: string, notes: string): Promise<WorkItemDayView> {
    const res = await api.put<WorkItemDayView>(`${BASE}/day/notes`, { date, notes });
    return res.data;
  },

  // 업무 기록 검색 (제목·작업 메모·특이사항). user_id 없으면 본인
  async search(params: WorkItemSearchParams): Promise<WorkItemSearchResult> {
    const res = await api.get<WorkItemSearchResult>(`${BASE}/search`, { params });
    return res.data;
  },

  // 업무 기록 PDF 내려받기 (검색 조건 그대로)
  async exportPdf(params: Omit<WorkItemSearchParams, "page" | "page_size">): Promise<void> {
    const response = await api.get(`${BASE}/export`, { params, responseType: "blob" });
    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const cd = response.headers["content-disposition"] as string | undefined;
    let filename = "work_history.pdf";
    if (cd) {
      const utf8 = cd.match(/filename\*=UTF-8''([^;]+)/i);
      const plain = cd.match(/filename="?([^";]+)"?/i);
      if (utf8) filename = decodeURIComponent(utf8[1]);
      else if (plain) filename = plain[1];
    }
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  // 월 보기(간트·달력) — 검색 조건 공용
  async month(params: { month: string; user_id?: string; q?: string; status?: string; linked?: boolean }): Promise<MonthView> {
    const res = await api.get<MonthView>(`${BASE}/month`, { params });
    return res.data;
  },

  // 날짜별 팀 보드
  async teamDay(date: string, departmentId?: number): Promise<TeamDayView> {
    const res = await api.get<TeamDayView>(`${BASE}/team-day`, {
      params: { date, department_id: departmentId || undefined },
    });
    return res.data;
  },

  // 직원 1명 기간 타임라인
  async teamTimeline(userId: string, from: string, to: string): Promise<TimelineView> {
    const res = await api.get<TimelineView>(`${BASE}/team-timeline`, { params: { user_id: userId, from, to } });
    return res.data;
  },

  // 팀 PDF (기간·부서)
  async exportTeamPdf(from: string, to: string, departmentId?: number): Promise<void> {
    const res = await api.get(`${BASE}/team-export`, {
      params: { from, to, department_id: departmentId || undefined },
      responseType: "blob",
    });
    downloadBlob(res, "team_work_history.pdf");
  },

  // 항목 상세 — 날짜별 작업 메모 타임라인
  async detail(id: number, userId?: string): Promise<WorkItemDetail> {
    const res = await api.get<WorkItemDetail>(`${BASE}/${id}/detail`, { params: userId ? { user_id: userId } : undefined });
    return res.data;
  },

  async linkCandidates(q = ""): Promise<WorkItemLinkCandidate[]> {
    const res = await api.get<WorkItemLinkCandidate[]>(`${BASE}/link-candidates`, { params: { q } });
    return res.data;
  },
};
